import React, { useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Provider, NavigationMenu, useAppBridge } from '@shopify/app-bridge-react';
import { Redirect } from '@shopify/app-bridge/actions';

/**
 * ClientRouter - Intercepts App Bridge navigation events (sidebar tab clicks)
 * and routes them through React Router instead of reloading the iframe.
 * Must be rendered inside both <BrowserRouter> and App Bridge <Provider>.
 */
function ClientRouter() {
  const app = useAppBridge();
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = app.subscribe(Redirect.Action.APP, ({ path }) => {
      navigate(path);
    });
    return unsubscribe;
  }, [app, navigate]);

  return null;
}

/**
 * AppBridgeProvider - Sets up Shopify App Bridge for embedded app functionality.
 * Includes ClientRouter so sidebar navigation uses client-side routing
 * instead of full iframe reloads.
 */
export function AppBridgeProvider({ children }) {
  // Get shop from URL params
  const searchParams = new URLSearchParams(window.location.search);
  const shop = searchParams.get('shop');
  const host = searchParams.get('host');

  const config = useMemo(() => {
    const apiKey = process.env.REACT_APP_SHOPIFY_API_KEY || '';

    return {
      apiKey,
      host: host || btoa(`${shop}/admin`),
      forceRedirect: true,
    };
  }, [host, shop]);

  // If no shop, render children without App Bridge (for development)
  if (!shop && process.env.NODE_ENV === 'development') {
    return <>{children}</>;
  }

  if (!shop) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Missing shop parameter</h2>
        <p>Please access this app through the Shopify admin.</p>
      </div>
    );
  }

  return (
    <Provider config={config}>
      <ClientRouter />
      <NavigationMenu
        navigationLinks={[
          {
            label: 'Analytics',
            destination: '/app/analytics',
          },
          {
            label: 'A/B Tests',
            destination: '/app/experiments',
          },
          {
            label: 'Billing',
            destination: '/app/billing',
          },
          {
            label: 'Settings',
            destination: '/app/settings',
          },
        ]}
      />
      {children}
    </Provider>
  );
}
